import type { SgwtChatbotConversation, SgwtChatbotMessage, SgwtChatbotUserHistory } from '../../widgets/sgwt-chatbot/shared/chatbot.types';
import type { ISgConnectUserInfo } from '../auth/bus-topics';

/**
 * CHANGELOG
 *
 * 1.2.0 - 2026-03-19
 *   - Add `DxAgentFactoryContext` type.
 *   - Rename `CustomAdapterContext`.
 *
 * 1.1.1 - 2025-12-19
 *   - Adding `conversation` argument on `onUserFeedback`
 *
 * 1.1.0 - 2025-12-17
 *   - Add support for user feedback
 *   - Rename `userMessage` to `onUserMessage`
 *   - `context: SgwtChatbotContext` is the first parameter and is not `undefined`
 *
 * 1.0.0
 *   - Initial version
 */

// Shared Configurations & Contexts
export type SgwtChatbotAdapterType = 'dx-assistant' | 'dx-agent-factory' | 'custom';

export type ChatbotSuggestion = { text: string; icon?: string; outlined?: boolean };

export interface SgwtChatbotConfiguration {
  adapter: SgwtChatbotAdapterType;

  /**
   * Initial message to display in the chat window.
   */
  initialMessage?: string;
  /**
   * The ID of the conversation to continue.
   */
  conversationId?: string;
  /**
   * Suggested topics to display in the chat window.
   */
  suggestions?: string[] | ChatbotSuggestion[];
}

export type SgwtChatbotContext = {
  /**
   * Info of the user connected.
   */
  user?: ISgConnectUserInfo;

  /**
   * Environment of the widget - bound to SG Connect environment.
   */
  environment: 'production' | 'homologation';
};

// Custom adapter -----------------

export interface CustomAdapterConfiguration extends SgwtChatbotConfiguration {
  adapter: 'custom';

  /**
   * Additional context properties, given to the widget by the application.
   */
  [key: string]: any;
}

export type CustomAdapterContext = SgwtChatbotContext & Omit<CustomAdapterConfiguration, 'adapter'>;

// Dx Assistant adapter -----------------

export interface DxAssistantConfiguration extends SgwtChatbotConfiguration {
  adapter: 'dx-assistant';

  // API environment to use. If not provided, it is 'homologation' or 'production' based on the widget environment.
  apiEnvironment?: 'development' | 'homologation' | 'production';

  // Application ID used for Feedback
  feedbackApplicationId?: string;

  // Knowledge space to use.
  knowledgeSpace: 'dx' | string;
  // documentation code, to limit knowledgeSpace (`web`...)
  scopes?: string[];
  // Filter (tags on documentation pages).
  tags?: string[];
  // User domain group, to customize answers.
  userDomainGroup?: string;
  // Ignore - for debug purposes.
  systemPromptOverride?: string;
}

export type DxAssistantContext = SgwtChatbotContext & Omit<DxAssistantConfiguration, 'adapter'>;

// Dx Agent Factory adapter -----------------

export interface DxAgentFactoryConfiguration extends SgwtChatbotConfiguration {
  adapter: 'dx-agent-factory';

  // API environment to use. If not provided, it is 'homologation' or 'production' based on the widget environment.
  apiEnvironment?: 'development' | 'homologation' | 'production';

  // Application ID used for Feedback
  feedbackApplicationId?: string;

  // ID of the agent.
  agentId: string;
}

export type DxAgentFactoryContext = SgwtChatbotContext & Omit<DxAgentFactoryConfiguration, 'adapter'>;

// ----------------------

export type AnyChatbotConfiguration = CustomAdapterConfiguration | DxAssistantConfiguration | DxAgentFactoryConfiguration;

export type AnyChatbotContext = CustomAdapterContext | DxAssistantContext | DxAgentFactoryContext;

// ----------------------
export type SgwtChatbotWidgetPublishMessageHandler = (message: SgwtChatbotMessage) => void;

/**
 * Interface for the SGWT Chatbot Adapter.
 */
export interface SgwtChatbotAdapter {
  get name(): string;

  /**
   * Handle the user message. It can either returns a response or `null` if the response will be handled asynchronously.
   * @param context the context of the chatbot.
   * @param userMessage the user message.
   * @returns a promise that resolves to the chatbot response or `null`.
   */
  onUserMessage: (context: SgwtChatbotContext, userMessage: SgwtChatbotMessage) => Promise<SgwtChatbotMessage | null>;

  /**
   * Handle a user feedback. If the adapter supports feedback, this method should be implemented.
   * @param context the context of the chatbot.
   * @param message the message on which the feedback is given.
   * @param reaction the reaction of the user ('like' or 'dislike').
   * @param explanations the explanations provided by the user.
   * @param conversation the conversation messages until the message on which the feedback is given. If newer messages exist, they are not included.
   * @returns a promise that resolves when the feedback is processed.
   */
  onUserFeedback?: (
    context: SgwtChatbotContext,
    message: SgwtChatbotMessage,
    reaction: 'like' | 'dislike',
    explanations: string,
    conversation: SgwtChatbotMessage[],
  ) => Promise<void>;

  /**
   * Get the user history.
   * @param context the context of the chatbot.
   * @returns the user history.
   */
  getHistory: (context: SgwtChatbotContext) => Promise<SgwtChatbotUserHistory>;

  /**
   * Get a conversation by its id.
   * @param context the context of the chatbot.
   * @param conversationId the id of the conversation.
   * @returns the user conversation or `null` if not found.
   */
  getConversation: (context: SgwtChatbotContext, conversationId: string) => Promise<SgwtChatbotConversation | null>;

  /**
   * Set a handler to publish messages in the conversation.
   * The widgets <sgwt-chatbot> will provide a way to the Adapter to send messages asynchronously to the chat.
   * @param handler
   * @returns
   */
  setPublishMessageHandler?: (handler: SgwtChatbotWidgetPublishMessageHandler) => void;
}

const isFunction = (value: any): boolean => {
  return typeof value === 'function';
};

// Check if the given Adapter correctly implements the SgwtChatbotAdapter interface
export function isValidAdapter(adapter: any): adapter is SgwtChatbotAdapter {
  return adapter && isFunction(adapter.onUserMessage) && isFunction(adapter.getHistory) && isFunction(adapter.getConversation);
}

/**
 * Some adapters may require an alternate token (i.e. a token provided by the other SG-Connect authority server) to call the API.
 * In such situation, the widget may has access to a function to retrieve this alternate token, that is set via the "setAlternateTokenGetterFunction" method.
 * This function should return the token or a promise resolving to the token.
 */
export type AlternateTokenGetterFunction = () => string | Promise<string>;
